import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Eye, EyeOff, Mail, Lock, User, Phone, Instagram, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CardContent } from "@/components/ui/card";
import partnersLogo from "@/assets/partners_logo.png";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import type { PartnerType, SignUpData } from "@/pages/AuthPage";

interface SignUpFormData {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  phone: string;
  socialMedia: string;
  acceptTerms: boolean;
}

interface SignUpFormProps {
  partnerType: PartnerType;
  onNext: (data: SignUpData) => void;
  onSwitchToLogin: () => void;
  onBack: () => void;
  defaultValues?: SignUpData | null;
}

export const SignUpForm: React.FC<SignUpFormProps> = ({ partnerType, onNext, onSwitchToLogin, onBack, defaultValues }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<SignUpFormData>({
    defaultValues: defaultValues ? {
      name: defaultValues.name,
      email: defaultValues.email,
      phone: defaultValues.phone,
      socialMedia: defaultValues.socialMedia,
    } : undefined,
  });

  const password = watch("password");

  // Instagram formatting function
  const formatInstagram = (value: string) => {
    // Remove all special characters except letters, numbers, and dots
    let cleaned = value.replace(/[^a-zA-Z0-9._]/g, "");
    // Remove leading @ if user types it
    cleaned = cleaned.replace(/^@/, "");
    // Convert to lowercase
    cleaned = cleaned.toLowerCase();
    // Add @ prefix
    return cleaned ? `@${cleaned}` : "";
  };

  // Phone formatting function
  const formatPhone = (value: string) => {
    // Remove all non-digits
    const cleaned = value.replace(/\D/g, "");
    // Limit to 11 digits (Brazilian phone format)
    const limited = cleaned.slice(0, 11);

    if (limited.length === 0) return "";
    if (limited.length <= 2) return `(${limited}`;
    if (limited.length <= 6) return `(${limited.slice(0, 2)}) ${limited.slice(2)}`;
    if (limited.length <= 10) return `(${limited.slice(0, 2)}) ${limited.slice(2, 7)}-${limited.slice(7)}`;
    return `(${limited.slice(0, 2)}) ${limited.slice(2, 7)}-${limited.slice(7, 11)}`;
  };

  const handleInstagramChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatInstagram(e.target.value);
    setValue("socialMedia", formatted);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhone(e.target.value);
    setValue("phone", formatted);
  };

  const onSubmit = (data: SignUpFormData) => {
    onNext({
      name: data.name,
      email: data.email,
      password: data.password,
      phone: data.phone,
      socialMedia: data.socialMedia,
      acceptTerms: data.acceptTerms,
    });
  };

  return (
    <div className="auth-glass w-full rounded-2xl border border-white/10 bg-black/40 shadow-2xl backdrop-blur-xl">
      <div className="text-center pt-8 pb-4 px-4">
        <img src={partnersLogo} alt="Silva Partners" className="mx-auto h-16 sm:h-20 mb-3" />
        <h1 className="text-2xl sm:text-3xl text-foreground" style={{ fontFamily: "AwesomeSerif, serif" }}>Silva Partners</h1>
        <p className="text-sm sm:text-base text-muted-foreground mt-1">
          {partnerType === "influencer" ? "Cadastro Influenciador" : "Cadastro Colaborador SILVA"}
        </p>
      </div>
      <CardContent className="px-6 sm:px-8 pb-8">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm font-medium">
                Nome
              </Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  {...register("name", {
                    required: "Nome é obrigatório",
                    minLength: {
                      value: 2,
                      message: "Nome deve ter pelo menos 2 caracteres",
                    },
                  })}
                  type="text"
                  id="name"
                  className="pl-10 h-11"
                  placeholder="Ex. Rafael Silva"
                />
              </div>
              {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
            </div>

            {/* Instagram */}
            <div className="space-y-2">
              <Label htmlFor="socialMedia" className="text-sm font-medium">
                Instagram
              </Label>
              <div className="relative">
                <Instagram className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  {...register("socialMedia", {
                    required: "Instagram é obrigatório",
                    pattern: {
                      value: /^@[a-z0-9._]+$/,
                      message: "Formato inválido. Use apenas letras, números, pontos e underscores",
                    },
                    minLength: {
                      value: 4,
                      message: "Instagram deve ter pelo menos 4 caracteres",
                    },
                  })}
                  type="text"
                  id="socialMedia"
                  className="pl-10 h-11"
                  placeholder="@seuinstagram"
                  onChange={handleInstagramChange}
                  onBlur={(e) => {
                    const formatted = formatInstagram(e.target.value);
                    setValue("socialMedia", formatted);
                  }}
                />
              </div>
              {errors.socialMedia && <p className="text-xs text-destructive">{errors.socialMedia.message}</p>}
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">
                Email
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  {...register("email", {
                    required: "Email é obrigatório",
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: "Email inválido",
                    },
                  })}
                  type="email"
                  id="email"
                  className="pl-10 h-11"
                  placeholder="seu@email.com"
                />
              </div>
              {errors.email && <p className="text-xs text-destructive">{errors.email.message}</p>}
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-sm font-medium">
                Telefone
              </Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  {...register("phone", {
                    required: "Telefone é obrigatório",
                    pattern: {
                      value: /^\(\d{2}\) \d{5}-\d{4}$/,
                      message: "Formato inválido. Use (XX) XXXXX-XXXX",
                    },
                  })}
                  type="tel"
                  id="phone"
                  className="pl-10 h-11"
                  placeholder="(21) 99999-9999"
                  onChange={handlePhoneChange}
                  onBlur={(e) => {
                    const formatted = formatPhone(e.target.value);
                    setValue("phone", formatted);
                  }}
                />
              </div>
              {errors.phone && <p className="text-xs text-destructive">{errors.phone.message}</p>}
            </div>

            {/* Password */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium">
                Senha
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  {...register("password", {
                    required: "Senha é obrigatória",
                    minLength: {
                      value: 6,
                      message: "Senha deve ter pelo menos 6 caracteres",
                    },
                  })}
                  type={showPassword ? "text" : "password"}
                  id="password"
                  className="pl-10 pr-12 h-11"
                  placeholder="••••••••"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-1 top-1/2 transform -translate-y-1/2 h-9 w-9 p-0 hover:bg-muted"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              {errors.password && <p className="text-xs text-destructive">{errors.password.message}</p>}
            </div>

            {/* Confirm Password */}
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-sm font-medium">
                Confirmar Senha
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  {...register("confirmPassword", {
                    required: "Confirmação de senha é obrigatória",
                    validate: (value: string) => value === password || "As senhas não coincidem",
                  })}
                  type={showConfirmPassword ? "text" : "password"}
                  id="confirmPassword"
                  className="pl-10 pr-12 h-11"
                  placeholder="••••••••"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-1 top-1/2 transform -translate-y-1/2 h-9 w-9 p-0 hover:bg-muted"
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              {errors.confirmPassword && (
                <p className="text-xs text-destructive">{errors.confirmPassword.message}</p>
              )}
            </div>
          </div>

          {/* Accept Terms Checkbox */}
          <div className="flex items-start gap-2">
            <Checkbox
              id="acceptTerms"
              {...register("acceptTerms", { required: true })}
              aria-invalid={!!errors.acceptTerms}
            />
            <label htmlFor="acceptTerms" className="text-sm select-none">
              <span className="text-orange-500 font-bold align-middle">*</span> Eu aceito os{" "}
              <Dialog>
                <DialogTrigger asChild>
                  <button type="button" className="text-orange-500 font-semibold underline hover:text-orange-600">
                    Termos e Condições de Uso
                  </button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Termos e Condições de Uso</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 text-sm">
                    <p>
                      <strong>Última atualização:</strong> {new Date().toLocaleDateString("pt-BR")}
                    </p>

                    <section>
                      <h3 className="font-semibold text-base mb-2">1. Aceitação dos Termos</h3>
                      <p className="mb-3">
                        Ao acessar e usar a plataforma Silva Partners, você concorda em cumprir e estar vinculado a
                        estes Termos e Condições de Uso. Se você não concordar com qualquer parte destes termos, não
                        poderá acessar ou usar nossos serviços.
                      </p>
                    </section>

                    <section>
                      <h3 className="font-semibold text-base mb-2">2. Descrição do Serviço</h3>
                      <p className="mb-3">
                        A Silva Partners é uma plataforma que conecta influenciadores digitais a oportunidades de ganho
                        de comissão através da promoção de produtos Silva Nutrition. Nossos serviços incluem:
                      </p>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>Geração de cupons de desconto personalizados</li>
                        <li>Sistema de rastreamento de vendas</li>
                        <li>Dashboard de análise de performance</li>
                        <li>Processamento de comissões</li>
                      </ul>
                    </section>

                    <section>
                      <h3 className="font-semibold text-base mb-2">3. Elegibilidade</h3>
                      <p className="mb-3">Para usar nossos serviços, você deve:</p>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>Ter pelo menos 18 anos de idade</li>
                        <li>Ter capacidade legal para celebrar contratos</li>
                        <li>Fornecer informações verdadeiras e precisas</li>
                        <li>Ter uma presença ativa em redes sociais</li>
                      </ul>
                    </section>

                    <section>
                      <h3 className="font-semibold text-base mb-2">4. Comissões e Pagamentos</h3>
                      <p className="mb-3">
                        As comissões são calculadas como 15% do valor total das vendas realizadas através do seu cupom,
                        após a dedução das taxas de frete. Os pagamentos são processados mensalmente, sujeitos aos
                        seguintes termos:
                      </p>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>Valor mínimo para saque: R$ 50,00</li>
                        <li>Comissões são pagas apenas sobre vendas concluídas</li>
                        <li>Reservamo-nos o direito de reter pagamentos em caso de violação dos termos</li>
                      </ul>
                    </section>

                    <section>
                      <h3 className="font-semibold text-base mb-2">5. Responsabilidades do Usuário</h3>
                      <p className="mb-3">Como influenciador, você concorda em:</p>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>Promover produtos de forma ética e honesta</li>
                        <li>Não fazer declarações falsas sobre os produtos</li>
                        <li>Respeitar as diretrizes de marketing da Silva Nutrition</li>
                        <li>Manter a confidencialidade de informações sensíveis</li>
                      </ul>
                    </section>

                    <section>
                      <h3 className="font-semibold text-base mb-2">6. Limitação de Responsabilidade</h3>
                      <p className="mb-3">
                        A Silva Partners não será responsável por danos indiretos, incidentais ou consequenciais
                        decorrentes do uso de nossos serviços, incluindo perda de lucros ou dados.
                      </p>
                    </section>

                    <section>
                      <h3 className="font-semibold text-base mb-2">7. Modificações</h3>
                      <p className="mb-3">
                        Reservamo-nos o direito de modificar estes termos a qualquer momento. As alterações entrarão em
                        vigor imediatamente após a publicação. O uso continuado dos serviços constitui aceitação dos
                        novos termos.
                      </p>
                    </section>

                    <section>
                      <h3 className="font-semibold text-base mb-2">8. Rescisão</h3>
                      <p className="mb-3">
                        Podemos encerrar ou suspender sua conta a qualquer momento, por qualquer motivo, incluindo
                        violação destes termos. Você também pode encerrar sua conta a qualquer momento através do painel
                        de controle.
                      </p>
                    </section>

                    <section>
                      <h3 className="font-semibold text-base mb-2">9. Lei Aplicável</h3>
                      <p className="mb-3">
                        Estes termos são regidos pelas leis brasileiras. Qualquer disputa será resolvida nos tribunais
                        competentes do Brasil.
                      </p>
                    </section>

                    <section>
                      <h3 className="font-semibold text-base mb-2">10. Contato</h3>
                      <p className="mb-3">
                        Para dúvidas sobre estes termos, entre em contato conosco através do email:
                        legal@silvapartners.com
                      </p>
                    </section>
                  </div>
                </DialogContent>
              </Dialog>{" "}
              e{" "}
              <Dialog>
                <DialogTrigger asChild>
                  <button type="button" className="text-orange-500 font-semibold underline hover:text-orange-600">
                    Política de Privacidade
                  </button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Política de Privacidade</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 text-sm">
                    <p>
                      <strong>Última atualização:</strong> {new Date().toLocaleDateString("pt-BR")}
                    </p>

                    <section>
                      <h3 className="font-semibold text-base mb-2">1. Informações que Coletamos</h3>
                      <p className="mb-3">
                        Coletamos as seguintes informações quando você se registra e usa nossa plataforma:
                      </p>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>
                          <strong>Informações pessoais:</strong> nome, email, telefone, perfil de redes sociais
                        </li>
                        <li>
                          <strong>Informações de conta:</strong> cupom personalizado, histórico de vendas
                        </li>
                        <li>
                          <strong>Dados de uso:</strong> interações com a plataforma, preferências
                        </li>
                        <li>
                          <strong>Informações técnicas:</strong> endereço IP, tipo de dispositivo, navegador
                        </li>
                      </ul>
                    </section>

                    <section>
                      <h3 className="font-semibold text-base mb-2">2. Como Usamos Suas Informações</h3>
                      <p className="mb-3">Utilizamos suas informações para:</p>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>Criar e gerenciar sua conta de influenciador</li>
                        <li>Processar comissões e pagamentos</li>
                        <li>Fornecer suporte ao cliente</li>
                        <li>Melhorar nossos serviços</li>
                        <li>Enviar comunicações importantes sobre sua conta</li>
                        <li>Cumprir obrigações legais</li>
                      </ul>
                    </section>

                    <section>
                      <h3 className="font-semibold text-base mb-2">3. Compartilhamento de Informações</h3>
                      <p className="mb-3">
                        Não vendemos, alugamos ou comercializamos suas informações pessoais. Podemos compartilhar
                        informações apenas nas seguintes situações:
                      </p>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>Com a Silva Nutrition para processamento de vendas</li>
                        <li>Com prestadores de serviços que nos ajudam a operar a plataforma</li>
                        <li>Quando exigido por lei ou ordem judicial</li>
                        <li>Para proteger nossos direitos e segurança</li>
                      </ul>
                    </section>

                    <section>
                      <h3 className="font-semibold text-base mb-2">4. Segurança dos Dados</h3>
                      <p className="mb-3">
                        Implementamos medidas de segurança técnicas e organizacionais para proteger suas informações
                        pessoais contra acesso não autorizado, alteração, divulgação ou destruição.
                      </p>
                    </section>

                    <section>
                      <h3 className="font-semibold text-base mb-2">5. Retenção de Dados</h3>
                      <p className="mb-3">
                        Mantemos suas informações pessoais pelo tempo necessário para fornecer nossos serviços, cumprir
                        obrigações legais, resolver disputas e fazer cumprir nossos acordos.
                      </p>
                    </section>

                    <section>
                      <h3 className="font-semibold text-base mb-2">6. Seus Direitos</h3>
                      <p className="mb-3">Você tem os seguintes direitos em relação aos seus dados pessoais:</p>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>
                          <strong>Acesso:</strong> solicitar informações sobre os dados que temos sobre você
                        </li>
                        <li>
                          <strong>Correção:</strong> solicitar correção de dados imprecisos
                        </li>
                        <li>
                          <strong>Exclusão:</strong> solicitar a exclusão de seus dados pessoais
                        </li>
                        <li>
                          <strong>Portabilidade:</strong> solicitar uma cópia de seus dados em formato legível
                        </li>
                        <li>
                          <strong>Oposição:</strong> opor-se ao processamento de seus dados
                        </li>
                      </ul>
                    </section>

                    <section>
                      <h3 className="font-semibold text-base mb-2">7. Cookies e Tecnologias Similares</h3>
                      <p className="mb-3">
                        Utilizamos cookies e tecnologias similares para melhorar sua experiência, analisar o uso da
                        plataforma e personalizar conteúdo. Você pode controlar o uso de cookies através das
                        configurações do seu navegador.
                      </p>
                    </section>

                    <section>
                      <h3 className="font-semibold text-base mb-2">8. Transferências Internacionais</h3>
                      <p className="mb-3">
                        Suas informações podem ser transferidas e processadas em países diferentes do Brasil. Garantimos
                        que essas transferências sejam feitas em conformidade com as leis de proteção de dados
                        aplicáveis.
                      </p>
                    </section>

                    <section>
                      <h3 className="font-semibold text-base mb-2">9. Menores de Idade</h3>
                      <p className="mb-3">
                        Nossos serviços não são destinados a menores de 18 anos. Não coletamos intencionalmente
                        informações pessoais de menores de idade. Se você é pai ou responsável e acredita que seu filho
                        nos forneceu informações pessoais, entre em contato conosco.
                      </p>
                    </section>

                    <section>
                      <h3 className="font-semibold text-base mb-2">10. Alterações na Política</h3>
                      <p className="mb-3">
                        Podemos atualizar esta política de privacidade periodicamente. Notificaremos você sobre mudanças
                        significativas através de email ou através de um aviso em nossa plataforma.
                      </p>
                    </section>

                    <section>
                      <h3 className="font-semibold text-base mb-2">11. Contato</h3>
                      <p className="mb-3">
                        Para exercer seus direitos ou esclarecer dúvidas sobre esta política, entre em contato conosco:
                      </p>
                      <ul className="list-none pl-0 space-y-1">
                        <li>
                          <strong>Email:</strong> privacidade@silvapartners.com
                        </li>
                        <li>
                          <strong>Endereço:</strong> [Endereço da empresa]
                        </li>
                        <li>
                          <strong>Telefone:</strong> [Telefone de contato]
                        </li>
                      </ul>
                    </section>
                  </div>
                </DialogContent>
              </Dialog>
              .
            </label>
          </div>
          {errors.acceptTerms && (
            <p className="text-xs sm:text-sm text-destructive mt-1">
              Você deve aceitar os Termos e Condições de Uso e Política de Privacidade.
            </p>
          )}

          <Button type="submit" className="w-full h-11 sm:h-12 text-sm sm:text-base font-medium">
            Continuar
          </Button>
        </form>

        <div className="mt-4 sm:mt-6 flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Voltar
          </Button>
          <p className="text-xs sm:text-sm text-muted-foreground">
            Já tem uma conta?{" "}
            <Button
              variant="link"
              onClick={onSwitchToLogin}
              className="p-0 h-auto font-medium text-primary hover:text-primary/80"
            >
              Faça login
            </Button>
          </p>
        </div>
      </CardContent>
    </div>
  );
};
